import ElasticAdapter from '@/features/shared/Adapters/ElasticAdapter';
import ElasticComponentContext, {
  ElasticComponentContextType,
} from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { withGTM } from '@/features/shared/HOCs';
import { useBotSearchParams, useElasticSearch } from '@/features/shared/Hooks';
import { LoadingStatusValues, SDL_DEFAULT_QPARAMS, SDL_SEARCH_FIELDS } from '@/utils/constants';
import {
  resolveBool,
  resolveColorName,
  resolveCompound,
  resolveImage,
  resolveRowOptsList,
  resolveRTC,
  resolveSpacingSize,
  resolveStatus,
  resolveTheme,
} from '@/utils/resolvers';
import qs from 'query-string';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import CMSWrapper from '../shared/CMSWrapper';
import { useSDLTableData } from './hooks/useSDLTableData';
import { CategoryContentType } from './IntroContent/IntroContent.interface';
import SDL from './SDL';
import { mapTarget, onDatalayer, onDatalayerCustom, onDatalayerV4Custom } from './SDL.datalayer';
import { DeviceType } from './SDL.types';
import { getDeviceType } from './SDLdetail/SDLdetail.utils';

type SearchProps = {
  q?: string;
  page?: string;
  sortBy?: string;
  sortType?: string;
  size?: string;
  category_type?: string;
  filters?: any;
  id?: string;
};

export type CategoryContentProps = { rawVal?: CategoryContentCompound[] };
export const resolveCategoryContentCompound = ({
  rawVal,
  page,
}: PropsWithCMSProps<CategoryContentProps>): CategoryContentType[] | undefined => {
  if (!rawVal) return undefined;

  return rawVal.map((category) => {
    const { categoryDescription, categoryIcon, categoryTitle, categoryType } =
      resolveCompound<CategoryContentCompound>({ rawVal: category }) || {};
    const icon = categoryIcon && (resolveImage({ rawVal: categoryIcon, page }) as ImageType);
    const description = categoryDescription?.value && resolveRTC({ rawVal: categoryDescription });

    return {
      categoryDescription: description,
      categoryIcon: icon,
      categoryTitle,
      categoryType,
    };
  });
};

const MappedSDL = ({
  content,
  presentation,
  contentButtons,
  columnHeadersAccessControl = {},
  columnHeadersCameras = {},
  columnHeadersIntercoms = {},
  columnHeadersStratocast = {},
  columnHeadersSaas = {},
  filterMenuAccessControl = {},
  filterMenuCameras = {},
  filterMenuIntercoms = {},
  filterMenuStratocast = {},
  filterMenuSaas = {},
  rowsPerPageOptions = null,
  labels,
  pageModels,
  config: baseConfig,
}: any) => {
  const { config: globalConfig } = pageModels;
  const { elasticsearch: elasticsearchConfig } = globalConfig || {};
  const { search_key: searchKey, sdl_engine: engine } = elasticsearchConfig || {};
  const facetsList = useRef(['category_type', 'id']);
  const [needsUpdate, setNeedsUpdate] = useState(true);
  const [needsExtUpdate, setNeedsExtUpdate] = useState(false);
  const [ableToSearch, setAbleToSearch] = useState(false);

  const { getContentCategoryHeaders, getContentCategoryFiltersLabels } = useSDLTableData({
    columnHeadersAccessControl,
    columnHeadersCameras,
    columnHeadersIntercoms,
    columnHeadersStratocast,
    columnHeadersSaas,
    filterMenuAccessControl,
    filterMenuCameras,
    filterMenuIntercoms,
    filterMenuStratocast,
    filterMenuSaas,
  });

  useBotSearchParams(!qs.parse(window.location.search)?.id);

  const {
    q = SDL_DEFAULT_QPARAMS.q,
    page = SDL_DEFAULT_QPARAMS.page,
    sortBy = SDL_DEFAULT_QPARAMS.sortBy,
    sortType = SDL_DEFAULT_QPARAMS.sortType,
    size = SDL_DEFAULT_QPARAMS.size,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_type = SDL_DEFAULT_QPARAMS.category_type,
    ...filters
  }: SearchProps = qs.parse(window.location.search);
  const defaultCategoryType: DeviceType = React.useMemo(() => {
    if (!filters?.id) return category_type as DeviceType;

    const SEPARATOR = '|';
    const [categoryType = category_type] = getDeviceType(filters?.id).split(SEPARATOR) || [];
    return categoryType as DeviceType;
  }, [category_type, filters]);

  const headerFilters = (category: string) =>
    Object.keys(getContentCategoryFiltersLabels(category));

  const [search, setSearch] = useState<SearchProps>({
    q,
    page,
    sortBy,
    sortType,
    size,
    category_type: defaultCategoryType,
    filters: ElasticAdapter.encodeFilters(filters, [
      ...(headerFilters(defaultCategoryType as string) as string[]),
      ...facetsList.current,
    ]),
  });

  const resetParams = () => {
    setSearch((state) => ({
      ...state,
      filters: {},
      sortBy: SDL_DEFAULT_QPARAMS.sortBy,
      sortType: SDL_DEFAULT_QPARAMS.sortType,
    }));
  };

  const cheaders = useMemo(
    () => getContentCategoryHeaders(search?.category_type as string),
    [getContentCategoryHeaders, search?.category_type],
  );

  const detailFields = useRef({
    basic_information: 'basic information',
    additional_information: 'additional information',
    firmware_information: 'firmware information',
    device_type: 'device type',
  });

  // If we get ID as queryParam then ignore the rest filters.
  const searchParams = useMemo(() => {
    const id = search?.filters?.id;

    return id ? { filters: { id }, q: '', page: '1', size: '1' } : search;
  }, [search]);

  const es = useElasticSearch({
    engine,
    searchParams,
    facets: headerFilters(search.category_type as string) as string[],
    searchKey,
    fields: {
      ...getContentCategoryHeaders(search.category_type as string),
      ...detailFields.current,
    },
    needsUpdate: needsUpdate || needsExtUpdate,
    hideFilters: ['category_type'],
    useDynamicFacets: false,
    searchFields: SDL_SEARCH_FIELDS,
    initBodyFilters: ['category_type'],
    filtersFallback: { category_type: search?.category_type || defaultCategoryType },
    ignoredResultFields: ['latest_firmware'],
    onlyPublished: false,
    clearResultOnFail: true,
  });

  const setQueryParam = (key: string, value: any, isFilter?: boolean): void => {
    if (isFilter) {
      setSearch((data: any) => ({
        ...data,
        filters: {
          ...data.filters,
          [key]: value,
        },
      }));
    } else {
      setSearch((data: any) => ({
        ...data,
        [key]: value,
      }));
      setNeedsUpdate(true);
    }
  };

  // Reload Facets when category_type changes
  useEffect(() => {
    es.reloadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.category_type]);

  useEffect(() => {
    if (needsUpdate && ableToSearch) {
      // eslint-disable-next-line no-restricted-globals
      history.pushState({}, '', `?${qs.stringify(ElasticAdapter.sanitizeParams(search))}`);
    }
    setAbleToSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsUpdate]);

  useEffect(() => {
    if (!es.isLoading) {
      setNeedsUpdate(false);
      setNeedsExtUpdate(false);
    }
  }, [es.isLoading]);

  const forceUpdate = () => {
    setNeedsUpdate(true);
  };

  const externalIDUpdate = () => {
    setNeedsExtUpdate(true);
  };

  const sdlContent = useMemo(
    () => ({
      ...content,
      searchKey,
      cheaders,
      presentation,
      labels,
      headerFiltersLabel: getContentCategoryFiltersLabels(search?.category_type || ''),
      config: { ...baseConfig, rowsPerPageOptions },
      isCached: es.isCached,
    }),
    [
      cheaders,
      baseConfig,
      content,
      getContentCategoryFiltersLabels,
      labels,
      presentation,
      search?.category_type,
      searchKey,
      es.isCached,
      rowsPerPageOptions,
    ],
  );
  const { containerBackgroundColor } = presentation;
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
    [containerBackgroundColor],
  );

  const allLabels = useMemo(
    () => ({
      ...labels,
      ...getContentCategoryFiltersLabels(search?.category_type || ''),
    }),
    [labels, getContentCategoryFiltersLabels, search?.category_type],
  );

  const { loading } = LoadingStatusValues;
  const [status, setStatus] = useState<ElasticComponentContextType['status']>(loading);
  useEffect(() => resolveStatus({ rawVal: es, isSearch: true, setStatus }), [es]);

  return (
    <div data-datalayer-position="sdl" data-datalayer-variant={presentation?.datalayerVariant}>
      <ElasticComponentContext.Provider
        value={{
          queryParams: search,
          updateQueryParams: setQueryParam,
          sortOptions: [],
          forceUpdate,
          externalIDUpdate,
          pagination: es.response.pagination,
          facets: es.facets,
          status,
          resetParams,
          engine,
          results: es.response.results,
          labels: allLabels,
          requestID: es.response?.meta?.request_id,
          searchKey,
        }}
      >
        <SDL
          theme={theme}
          content={sdlContent}
          presentation={presentation}
          contentButtons={contentButtons}
          labels={allLabels}
        />
      </ElasticComponentContext.Provider>
    </div>
  );
};

const MappedSDLGTMclick = withGTM({
  Component: MappedSDL,
  onDatalayer,
  config: { mapTarget },
});
const SDLCustomV4GTM = withGTM({
  Component: MappedSDLGTMclick,
  onDatalayer: onDatalayerV4Custom,
  config: {
    eventName: 'gtm:sdl:search',
    isCustom: true,
  },
});
const SDLCustomGTM = withGTM({
  Component: SDLCustomV4GTM,
  onDatalayer: onDatalayerCustom,
  config: {
    eventName: 'gtm:custom:sdl',
    isCustom: true,
  },
});

const SDLCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        backgroundBanner: [null, resolveImage],
        backgroundBannerVideo: [null, resolveCompound],
        categoryContentCompound: [null, resolveCategoryContentCompound],
        collapseDisclaimer: [null, resolveBool],
        description: [null, resolveRTC],
        disclaimer: [null, resolveRTC],
        heading: null,
        includeBreadcrumb: [null, resolveBool],
        mobileBackgroundBanner: [null, resolveImage],
        quickLinks: [null, resolveRTC],
      },
    ],
    'value-lists/sdl-column-headers-cameras': 'columnHeadersCameras',
    'value-lists/sdl-column-headers-intercoms': 'columnHeadersIntercoms',
    'value-lists/sdl-column-headers-access-control': 'columnHeadersAccessControl',
    'value-lists/sdl-column-headers-stratocast': 'columnHeadersStratocast',
    'value-lists/sdl-column-headers-saas': 'columnHeadersSaas',
    'value-lists/sdl-list-menu-cameras': 'filterMenuCameras',
    'value-lists/sdl-list-menu-intercoms': 'filterMenuIntercoms',
    'value-lists/sdl-list-menu-access-control': 'filterMenuAccessControl',
    'value-lists/sdl-list-menu-stratocast': 'filterMenuStratocast',
    'value-lists/sdl-list-menu-saas': 'filterMenuSaas',
    'value-lists/sdl-rows-per-page-options': ['rowsPerPageOptions', resolveRowOptsList],
    componentParameterMap: [
      'presentation',
      {
        'bottom-spacing': ['containerBottomSpacing', resolveSpacingSize],
        'top-spacing': ['containerTopSpacing', resolveSpacingSize],
        'container-background-color': ['containerBackgroundColor', resolveColorName],
        'variant-label': 'datalayerVariant',
      },
    ],
    'sdl.labels': 'labels',
    'sdl.config': 'config',
  });

  const requiredFields = useRef({
    document: '<b>Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={SDLCustomGTM}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default memo(SDLCMSWrapper);
